import {CompanyMinimumRes, UserDetailsMinRes, UserDetailsRes} from '../cp';
import {BudgetCapacityInterval, BudgetCapacityUnit, BudgetCarryOverType, BudgetOveragePolicy, BudgetPricingModel, BudgetRecurrenceType, ProjectRole, TrackerType} from '../../../enums';
import {InvoiceDocumentFormat} from '../../../enums/invoice-document-format';
import {TimesheetTemplate} from '../../../enums/timesheet-template';
import {TimesheetSignee} from '../../../enums/timesheet-signee';
import {Type} from 'class-transformer';
import {InvoiceCreateReq} from '../cp/invoice.contract';
import {BaseTaskCreateReq} from './task';
import {ProjectContractProjectReq} from './project-contract.contract';
import {TrackerCreateReq} from './tracker/tracker.contract';
import {ProjectMinRes, ProjectTrackerRes} from './tracker/project-tracker';
import moment from "moment";
import {WorkflowStatusMinRes} from './status.contract';
import {
    ColumnType,
    yascAutocomplete,
    yascClassDisplayName,
    yascDisplayName,
    yascFormGrid,
    yascFormIgnore,
    yascOptionsKey,
    yascRequired,
    yascTableColumn,
    yascTableDisplayFn,
    yascTableIgnore
} from '@yasc/yasc-material-components';


export class ProjectBudgetRes {
    id!: number;
    name = '';
    reference = '';
    orderNumber = '';
    userId?: number | null;
    @Type(() => UserDetailsMinRes)
    user?: UserDetailsMinRes | null;
    sourceOfferId?: number | null;
    sourceOfferNumber = '';
    start!: string;
    end?: string | null;
    pricingModel: BudgetPricingModel = BudgetPricingModel.TimeAndMaterial;
    currencyCode = 'EUR';
    plannedHours?: number | null;
    includedHours?: number | null;
    defaultHourlyRate?: number | null;
    fixedPrice?: number | null;
    amountLimit?: number | null;
    recurrenceType: BudgetRecurrenceType = BudgetRecurrenceType.None;
    recurrenceInterval?: number | null;
    carryOverType: BudgetCarryOverType = BudgetCarryOverType.None;
    carryOverLimitHours?: number | null;
    carryOverLimitAmount?: number | null;
    carryOverPercentage?: number | null;
    carryOverExpirationPeriods?: number | null;
    overagePolicy: BudgetOveragePolicy = BudgetOveragePolicy.Billable;
    overageHourlyRate?: number | null;
    contractId?: number | null;
    contractNumber = '';
    capacityInterval: BudgetCapacityInterval = BudgetCapacityInterval.None;
    capacityUnit: BudgetCapacityUnit = BudgetCapacityUnit.Hours;
    capacityAmount?: number | null;
    hoursPerDay = 8;
    consumptionFactor = 1;
    manualOnly = false;
    totalHours = 0;
    usedHours = 0;
    remainingHours = 0;
    invoicedHours = 0;
    actualWorkedHours = 0;
    operationalRemainingHours = 0;
    unbilledWorkedHours = 0;
    hourlyRate = 0;
    totalNetto = 0;
    usedNetto = 0;
    remainingNetto = 0;
    actualNetto = 0;
    operationalRemainingNetto = 0;
    isActive = true;
    comment = '';
}

export class ProjectBudgetReq {
    id?: number | null;
    name = '';
    reference?: string | null;
    orderNumber?: string | null;
    userId?: number | null;
    sourceOfferId?: number | null;
    start!: string;
    end?: string | null;
    pricingModel: BudgetPricingModel = BudgetPricingModel.TimeAndMaterial;
    currencyCode = 'EUR';
    plannedHours?: number | null;
    includedHours?: number | null;
    defaultHourlyRate?: number | null;
    fixedPrice?: number | null;
    amountLimit?: number | null;
    recurrenceType: BudgetRecurrenceType = BudgetRecurrenceType.None;
    recurrenceInterval?: number | null;
    carryOverType: BudgetCarryOverType = BudgetCarryOverType.None;
    carryOverLimitHours?: number | null;
    carryOverLimitAmount?: number | null;
    carryOverPercentage?: number | null;
    carryOverExpirationPeriods?: number | null;
    overagePolicy: BudgetOveragePolicy = BudgetOveragePolicy.Billable;
    overageHourlyRate?: number | null;
    contractId?: number | null;
    capacityInterval: BudgetCapacityInterval = BudgetCapacityInterval.None;
    capacityUnit: BudgetCapacityUnit = BudgetCapacityUnit.Hours;
    capacityAmount?: number | null;
    hoursPerDay?: number | null;
    consumptionFactor?: number | null;
    manualOnly = false;
    isActive = true;
    comment?: string | null;
}

@yascClassDisplayName('projectTabTeamTitle')
export class ProjectRoleRes {
    @yascDisplayName('general_backendId')
    @yascFormIgnore()
    @yascTableIgnore(true)
    id!: number

    @Type(() => UserDetailsRes)
    @yascDisplayName('general_employee')
    @yascTableColumn(ColumnType.Autocomplete)
    @yascTableDisplayFn((value?: any) => {
        const user = value?.user ?? value;
        return [user?.givenName, user?.lastName].filter(Boolean).join(' ').trim() || user?.email || '';
    })
    user!: UserDetailsMinRes;

    get username(): string {
        return [this.user?.givenName, this.user?.lastName].filter(Boolean).join(' ').trim() || this.user?.email || '';
    }

    @yascDisplayName('general_role')
    @yascTableColumn(ColumnType.Select)
    @yascTableDisplayFn((value?: any) => {
        const role = value?.role ?? value;
        return role === undefined || role === null ? '' : ProjectRole[role]?.toString() ?? role.toString();
    })
    role!: ProjectRole;
}

export class ProjectCreateReq extends TrackerCreateReq {
    override type: TrackerType = TrackerType.Project;
    requireTasks: boolean = false;
    requireProjectBudget: boolean = false;
    requireEfforts: boolean = false;
    customerId!: number;
    timesheetCustomerId?: number | null;
    orderNr?: string;
    offerNr?: string;
    billingPaymentTermsDays?: number | null;
    billingPaymentDiscountPercent?: number | null;
    billingPaymentDiscountDays?: number | null;
    billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;
    timesheetTemplate?: TimesheetTemplate | null;
    timesheetSignee?: TimesheetSignee | null;
    comment?: string;
    nextBill?: any;
    start?: string;
    end?: string;
    projectVolumeBrutto?: number
    toDoGroupId?: number;

    // only used for showing data - ignored by backend
    projectVolumeBruttoOpen: number = 0;

    @Type(() => ProjectTeamMemberReq)
    teamMembers: ProjectTeamMemberReq[] = []

    @Type(() => ProjectBudgetReq)
    budgets: ProjectBudgetReq[] = [];

    @Type(() => ProjectContractProjectReq)
    contracts: ProjectContractProjectReq[] = [];

    @Type(() => InvoiceCreateReq)
    invoices: InvoiceCreateReq[] = [];

    @Type(() => BaseTaskCreateReq)
    tasks: BaseTaskCreateReq[] = [];

    constructor(value: Partial<ProjectCreateReq> = {}) {
        super();
        Object.assign(this, value);
    }

    static FromDetails(value: Partial<ProjectTrackerRes>) {
        return new ProjectCreateReq({
            name: value.name,
            offerNr: value.offerNr,
            orderNr: value.orderNr,
            billingPaymentTermsDays: value.billingPaymentTermsDays ?? null,
            billingPaymentDiscountPercent: value.billingPaymentDiscountPercent ?? null,
            billingPaymentDiscountDays: value.billingPaymentDiscountDays ?? null,
            billingPreferredInvoiceFormat: value.billingPreferredInvoiceFormat ?? null,
            timesheetTemplate: value.timesheetTemplate ?? null,
            timesheetSignee: value.timesheetSignee ?? null,
            comment: value.comment,
            customerId: value.customer!.id,
            timesheetCustomerId: value.timesheetCustomer?.id ?? null,
            invoices: value.invoices ? value.invoices.map(x => InvoiceCreateReq.FromDetails(x)) : [],
            projectVolumeBrutto: value.projectVolumeBrutto,
            projectVolumeBruttoOpen: value.projectVolumeBruttoOpen,
            teamMembers: value.teamMembers ? value.teamMembers.map(x => ProjectTeamMemberReq.FromDetails(x)) : [],
            workflowId: value.workflow?.id,
            currentStatusId: value.currentWorkflowStatus?.id,
            toDoGroupId: value.toDos?.id
        })

    }

    static FromMinDetails(value: Partial<ProjectMinRes>) {
        return new ProjectCreateReq({
            name: value.name,
            offerNr: value.offerNr,
            orderNr: value.orderNr,
            billingPaymentTermsDays: value.billingPaymentTermsDays ?? null,
            billingPaymentDiscountPercent: value.billingPaymentDiscountPercent ?? null,
            billingPaymentDiscountDays: value.billingPaymentDiscountDays ?? null,
            billingPreferredInvoiceFormat: value.billingPreferredInvoiceFormat ?? null,
            timesheetTemplate: value.timesheetTemplate ?? null,
            timesheetSignee: value.timesheetSignee ?? null,
            comment: value.comment,
            customerId: value.customer!.id,
            timesheetCustomerId: value.timesheetCustomer?.id ?? null,
            projectVolumeBrutto: value.projectVolumeBrutto,
            projectVolumeBruttoOpen: value.projectVolumeBruttoOpen,
            currentStatusId: value.currentWorkflowStatus?.id,
            workflowId: value.workflowId,
        })

    }
}


export class ProjectRoleUpdateReq {
    id!: number;
    role!: ProjectRole;

    constructor(id: number, role: ProjectRole) {
        this.id = id;
        this.role = role;
    }
}

abstract class BaseUpdateReq {
    projectId!: number

    constructor(projId: number) {
        this.projectId = projId
    }
}

@yascClassDisplayName('projectTabTeamTitle')
export class ProjectTeamMemberReq {
    @yascDisplayName('general_employee')
    @yascTableColumn(ColumnType.Autocomplete)
    @yascAutocomplete()
    @yascOptionsKey('projectTeamUsers')
    @yascRequired()
    @yascFormGrid({ row: 0, width: 2, fullRow: true })
    userId: number | null;

    @yascDisplayName('general_role')
    @yascTableColumn(ColumnType.Select)
    @yascOptionsKey('projectRoles')
    @yascRequired()
    @yascFormGrid({ row: 1, width: 2, fullRow: true })
    roleId: ProjectRole | null;

    constructor(value: Partial<ProjectTeamMemberReq>) {
        this.userId = value.userId ?? null;
        this.roleId = value.roleId ?? null;
    }

    static FromDetails(value: Partial<ProjectRoleRes>) {
        return new ProjectTeamMemberReq({
            userId: value.user?.id,
            roleId: value.role
        })
    }
}

export class ProjectUpdateReq {
    id!: number;
    name!: string;
    internalName?: string | null;
    customerId!: number;
    timesheetCustomerId?: number | null;
    requireTasks!: boolean;
    requireProjectBudget!: boolean;
    requireEfforts!: boolean;
    orderNr?: string | null;
    offerNr?: string | null;
    billingPaymentTermsDays?: number | null;
    billingPaymentDiscountPercent?: number | null;
    billingPaymentDiscountDays?: number | null;
    billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;
    timesheetTemplate?: TimesheetTemplate | null;
    timesheetSignee?: TimesheetSignee | null;
    projectVolumeBrutto!: number;
    currentStatusId!: number;
    workflowId!: number;
    comment?: string | null;
    start?: string | null;
    end?: string | null;
    color?: string | null;

    @Type(() => ProjectTeamMemberReq)
    teamMembers: ProjectTeamMemberReq[] = []

    @Type(() => ProjectBudgetReq)
    budgets: ProjectBudgetReq[] = [];

    @Type(() => ProjectContractProjectReq)
    contracts: ProjectContractProjectReq[] = [];

    _nextBill?: any;

    // only used for showing data - ignored by backend
    projectVolumeBruttoOpen?: number;

    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = moment(value);
    }

    public FromMinDetails(value: Partial<ProjectMinRes>) {
        console.log(value);
        let test = new ProjectUpdateReq({
            name: value.name,
            offerNr: value.offerNr,
            orderNr: value.orderNr,
            billingPaymentTermsDays: value.billingPaymentTermsDays ?? null,
            billingPaymentDiscountPercent: value.billingPaymentDiscountPercent ?? null,
            billingPaymentDiscountDays: value.billingPaymentDiscountDays ?? null,
            billingPreferredInvoiceFormat: value.billingPreferredInvoiceFormat ?? null,
            timesheetTemplate: value.timesheetTemplate ?? null,
            timesheetSignee: value.timesheetSignee ?? null,
            comment: value.comment,
            projectVolumeBrutto: value.projectVolumeBrutto,
            projectVolumeBruttoOpen: value.projectVolumeBruttoOpen,
            currentWorkflowStatus: value.currentWorkflowStatus,
            workflowId: value.workflowId,
            type: value.type,

        })
        console.log(test)
        return test
    }

    constructor(res?: Partial<ProjectTrackerRes>) {
        if (res === undefined || res === null) {
            return
        }
        if (res.id)
            this.id = res.id;
        if (res.customer)
            this.customerId = res.customer.id;
        this.timesheetCustomerId = res.timesheetCustomer?.id ?? null;
        if (res.orderNr)
            this.orderNr = res.orderNr;
        if (res.offerNr)
            this.offerNr = res.offerNr;
        this.billingPaymentTermsDays = res.billingPaymentTermsDays ?? null;
        this.billingPaymentDiscountPercent = res.billingPaymentDiscountPercent ?? null;
        this.billingPaymentDiscountDays = res.billingPaymentDiscountDays ?? null;
        this.billingPreferredInvoiceFormat = res.billingPreferredInvoiceFormat ?? null;
        this.timesheetTemplate = res.timesheetTemplate ?? null;
        this.timesheetSignee = res.timesheetSignee ?? null;
        if (res.projectVolumeBrutto !== undefined && res.projectVolumeBrutto !== null)
            this.projectVolumeBrutto = res.projectVolumeBrutto
        if (res.projectVolumeBruttoOpen !== undefined && res.projectVolumeBruttoOpen !== null)
            this.projectVolumeBruttoOpen = res.projectVolumeBruttoOpen;
        if (res.nextBill)
            this.nextBill = res.nextBill;
        if (res.currentWorkflowStatus)
            this.currentStatusId = res.currentWorkflowStatus.id;
        if (res.name)
            this.name = res.name;
        if (res.requireTasks !== undefined && res.requireTasks !== null)
            this.requireTasks = res.requireTasks;
        if (res.requireProjectBudget !== undefined && res.requireProjectBudget !== null)
            this.requireProjectBudget = res.requireProjectBudget;
        // if (res.workflow)
        //     this.workflowId = res.workflow.id;
        if (res.workflowId !== undefined && res.workflowId !== null)
            this.workflowId = res.workflowId;
        if (res.comment)
            this.comment = res.comment;
        if (res.teamMembers)
            this.teamMembers = res.teamMembers.map(e => new ProjectTeamMemberReq({userId: e.user.id, roleId: e.role}));
        // Object.assign(this, res)
    }


}

export class ProjectToDoUpdateReq extends BaseUpdateReq {
    toDoGroupId: number | null

    constructor(projId: number, toDoid: number | null) {
        super(projId);
        this.toDoGroupId = toDoid
    }
}

export class ProjectDisplayIdUpdateReq extends BaseUpdateReq {
    newId!: string

    constructor(newId: string, projId: number) {
        super(projId);
        this.newId = newId
    }
}

export class ProjectBillUpdateReq extends BaseUpdateReq {
    billId!: number;

    constructor(billId: number, projId: number) {
        super(projId);
        this.billId = billId
    }
}

export class ProjectSummaryAggregationRes{

}

export class ProjectSummaryGroupingRes{
    availableWorkflows:WorkflowStatusMinRes[] = []
    availableCompanies: CompanyMinimumRes[] = []
}
